import type { ILanguageModel } from "@kuika/kuika-cl-model";
import { DATASOURCE_TYPE_DISPLAY_NAMES } from "@kuika/kuika-cl-model/src/enums/datasource-type";
import type { MentionCategory, MentionReference, MentionSuggestion } from "../shared/mcp-chat-types";

// ── Narrow type stubs for the screen designer state (accessed via getSdInstance) ──

interface SdDisplayItemProperty {
  name: string;
  selectedType?: number;
  value?: string | number | boolean;
  symbol?: { fixedValue?: string | number | boolean };
}

interface SdDisplayItem {
  id: number;
  friendlyName?: string;
  parentId?: number | null;
  item?: {
    name: string;
    properties?: SdDisplayItemProperty[];
  };
  children?: SdDisplayItem[];
  styleObj?: Record<string, Record<string, string | number | undefined>>;
}

interface SdDatasource {
  id: string | number;
  name?: string;
  datasourceType?: number;
  type?: string;
  tables?: Array<{ name?: string; tableName?: string }>;
  schemas?: Array<{ name?: string; tableName?: string }>;
}

interface SdActionDef {
  id: string | number;
  name?: string;
  displayName?: string;
  actionType?: number;
  kDatasource_Id?: string | number | null;
  statement?: string;
  parameters?: Array<{ name?: string; parameterName?: string; type?: string }>;
  actionParameters?: Array<{ name?: string; parameterName?: string; type?: string }>;
}

interface SdValidationError {
  displayItem: string;
  displayItemId?: number;
  event: string;
  message: string;
  screenName: string;
  workflowName: string;
  workflowItem: string;
  type: string;
  schedulerJobName?: string;
  apiManagementName?: string;
  csharpDatasourceName: string | null;
  csharpErrorMessages: string[] | null;
}

interface SdScreen {
  id: string | number;
  name?: string;
}

interface SdInstance {
  screenProvider?: {
    state?: {
      screenDesignDictionary?: Record<number, SdDisplayItem>;
      screenList?: SdScreen[];
      sdId?: string | number | null;
    };
  };
  datasourceProvider?: {
    state?: {
      datasources?: SdDatasource[];
    };
  };
  state?: {
    datasources?: SdDatasource[];
  };
  projectProvider?: {
    state?: {
      actionDefinitions?: SdActionDef[];
      validationErrors?: SdValidationError[];
      project?: {
        startingScreenId?: string | number;
        signinScreenId?: string | number;
        languages?: ILanguageModel[];
      };
    };
    getProviderValues?: () => { actionDefinitions?: SdActionDef[] };
    getProject?: () => {
      startingScreenId?: string | number;
      signinScreenId?: string | number;
      languages?: ILanguageModel[];
    } | null;
  };
}

// ── Constants ──

const STRUCTURAL_COMPONENTS = new Set([
  "TableRow",
  "TableHeader",
  "ReportTableRow",
  "ReportTableHeader",
  "StaticTableRow",
  "StaticTableHeader",
  "GalleryViewFlowElement"
]);

const MAX_UNFILTERED = 50;
const MAX_FILTERED = 15;

const CATEGORY_ICONS: Record<MentionCategory, string> = {
  component: "widgets",
  datasource: "storage",
  action: "bolt",
  screen: "web",
  error: "warning",
  language: "translate"
};

function resolveDatasourceTypeName(typeValue: number | undefined): string | null {
  if (typeValue == null) return null;
  return DATASOURCE_TYPE_DISPLAY_NAMES[typeValue] ?? `Type ${typeValue}`;
}

const KEY_STYLE_PATHS: Array<{ section: string; key: string }> = [
  { section: "fill", key: "backgroundColor" },
  { section: "text", key: "color" },
  { section: "text", key: "fontSize" },
  { section: "text", key: "fontWeight" },
  { section: "position", key: "width" },
  { section: "position", key: "height" }
];

// ── SD instance accessor ──

function getSd(): SdInstance | null {
  try {
    const win = window as Window & { getSdInstance?: () => SdInstance };
    return win.getSdInstance?.() ?? null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Component gathering
// ---------------------------------------------------------------------------

interface ComponentEntry {
  kuikaId: string;
  displayName: string;
  componentType: string;
  parentName: string | null;
  di: SdDisplayItem;
}

function walkDisplayItems(di: SdDisplayItem, parentName: string | null, results: ComponentEntry[]): void {
  const componentType: string = di.item?.name || "Unknown";

  if (!STRUCTURAL_COMPONENTS.has(componentType)) {
    const displayName = di.friendlyName || componentType;
    results.push({
      kuikaId: di.id?.toString() ?? "",
      displayName,
      componentType,
      parentName,
      di
    });
  }

  if (di.children && di.children.length > 0) {
    const currentName = di.friendlyName || componentType;
    for (const child of di.children) {
      walkDisplayItems(child, currentName, results);
    }
  }
}

function gatherComponents(sd: SdInstance): MentionSuggestion[] {
  const dict = sd.screenProvider?.state?.screenDesignDictionary;
  if (!dict) return [];

  const rootDi = dict[-1];
  if (!rootDi) return [];

  const entries: ComponentEntry[] = [];
  if (rootDi.children) {
    for (const child of rootDi.children) {
      walkDisplayItems(child, null, entries);
    }
  }

  return entries.map((entry) => ({
    category: "component" as MentionCategory,
    entityId: entry.kuikaId,
    displayName: entry.displayName,
    secondaryText: entry.parentName ? `in ${entry.parentName}` : entry.componentType,
    icon: CATEGORY_ICONS.component
  }));
}

// ---------------------------------------------------------------------------
// Datasource gathering
// ---------------------------------------------------------------------------

function getDatasources(sd: SdInstance): SdDatasource[] {
  return sd.datasourceProvider?.state?.datasources || sd.state?.datasources || [];
}

function gatherDatasources(sd: SdInstance): MentionSuggestion[] {
  const datasources = getDatasources(sd);

  return datasources.map((ds) => {
    const typeName = resolveDatasourceTypeName(ds.datasourceType);
    return {
      category: "datasource" as MentionCategory,
      entityId: ds.id?.toString() ?? "",
      displayName: ds.name || "Unnamed",
      secondaryText: typeName ?? "Datasource",
      icon: CATEGORY_ICONS.datasource
    };
  });
}

// ---------------------------------------------------------------------------
// Action gathering
// ---------------------------------------------------------------------------

function getActionDefinitions(sd: SdInstance): SdActionDef[] {
  return (
    sd.projectProvider?.state?.actionDefinitions || sd.projectProvider?.getProviderValues?.()?.actionDefinitions || []
  );
}

function resolveDatasourceName(sd: SdInstance, datasourceId: string | number | null | undefined): string | null {
  if (!datasourceId) return null;
  const ds = getDatasources(sd).find((d) => String(d.id) === String(datasourceId));
  return ds?.name ?? null;
}

function gatherActions(sd: SdInstance): MentionSuggestion[] {
  const actionDefs = getActionDefinitions(sd);

  return actionDefs.map((a) => {
    const parentDsName = resolveDatasourceName(sd, a.kDatasource_Id);
    const secondaryParts: string[] = [];
    if (parentDsName) secondaryParts.push(parentDsName);
    const actionTypeName = resolveDatasourceTypeName(a.actionType);
    if (actionTypeName) secondaryParts.push(actionTypeName);

    return {
      category: "action" as MentionCategory,
      entityId: a.id?.toString() ?? "",
      displayName: a.displayName || a.name || "Unnamed",
      secondaryText: secondaryParts.length > 0 ? secondaryParts.join(" | ") : "Action",
      icon: CATEGORY_ICONS.action
    };
  });
}

// ---------------------------------------------------------------------------
// Screen gathering
// ---------------------------------------------------------------------------

function gatherScreens(sd: SdInstance): MentionSuggestion[] {
  const screenList = sd.screenProvider?.state?.screenList || [];
  const currentScreenId = sd.screenProvider?.state?.sdId ?? null;

  return screenList.map((screen) => {
    const isCurrent = String(screen.id) === String(currentScreenId);
    const secondaryParts: string[] = [];
    if (isCurrent) secondaryParts.push("Current");

    return {
      category: "screen" as MentionCategory,
      entityId: screen.id?.toString() ?? "",
      displayName: screen.name || "Unnamed",
      secondaryText: secondaryParts.length > 0 ? secondaryParts.join(" | ") : "Screen",
      icon: CATEGORY_ICONS.screen
    };
  });
}

// ---------------------------------------------------------------------------
// Validation error gathering
// ---------------------------------------------------------------------------

function getValidationErrors(sd: SdInstance): SdValidationError[] {
  return sd.projectProvider?.state?.validationErrors || [];
}

function gatherValidationErrors(sd: SdInstance): MentionSuggestion[] {
  const errors = getValidationErrors(sd);
  if (errors.length === 0) return [];

  return errors.map((err, idx) => {
    const severity = err.type === "Error" ? "Error" : "Warning";
    const context = err.screenName || err.workflowName || "";
    const truncatedMsg = err.message.length > 60 ? err.message.substring(0, 57) + "..." : err.message;

    return {
      category: "error" as MentionCategory,
      entityId: `validation-${idx}`,
      displayName: `${severity}: ${truncatedMsg}`,
      secondaryText: [err.displayItem, context].filter(Boolean).join(" | ") || severity,
      icon: CATEGORY_ICONS.error
    };
  });
}

// ---------------------------------------------------------------------------
// Language gathering
// ---------------------------------------------------------------------------

function getProjectLanguages(sd: SdInstance): ILanguageModel[] {
  const project = sd.projectProvider?.getProject?.() || sd.projectProvider?.state?.project;
  return project?.languages || [];
}

function gatherLanguages(sd: SdInstance): MentionSuggestion[] {
  const languages = getProjectLanguages(sd);
  if (languages.length === 0) return [];

  return languages.map((lang) => ({
    category: "language" as MentionCategory,
    entityId: lang.id,
    displayName: lang.shortName || lang.name,
    secondaryText: lang.isDefault ? `${lang.name} (Default)` : lang.name,
    icon: CATEGORY_ICONS.language
  }));
}

// ---------------------------------------------------------------------------
// Public: gatherMentionSuggestions
// ---------------------------------------------------------------------------

export function gatherMentionSuggestions(query: string): MentionSuggestion[] {
  const sd = getSd();
  if (!sd) return [];

  const all: MentionSuggestion[] = [
    ...gatherComponents(sd),
    ...gatherDatasources(sd),
    ...gatherActions(sd),
    ...gatherScreens(sd),
    ...gatherLanguages(sd),
    ...gatherValidationErrors(sd)
  ];

  if (!query) {
    return all.slice(0, MAX_UNFILTERED);
  }

  const lowerQuery = query.toLowerCase();
  const filtered = all.filter(
    (s) => s.displayName.toLowerCase().includes(lowerQuery) || s.secondaryText.toLowerCase().includes(lowerQuery)
  );

  return filtered.slice(0, MAX_FILTERED);
}

// ---------------------------------------------------------------------------
// Context expansion helpers
// ---------------------------------------------------------------------------

function findDisplayItemById(dict: Record<number, SdDisplayItem>, kuikaId: string): SdDisplayItem | null {
  const numericId = parseInt(kuikaId, 10);
  if (dict[numericId]) return dict[numericId];

  for (const key of Object.keys(dict)) {
    const di = dict[Number(key)];
    if (di?.id?.toString() === kuikaId) return di;
  }
  return null;
}

function extractKeyProperties(di: SdDisplayItem): Array<{ name: string; value: string }> {
  const properties = di.item?.properties || [];
  const result: Array<{ name: string; value: string }> = [];

  for (const prop of properties) {
    if (result.length >= 8) break;
    const fixedValue = prop.symbol?.fixedValue ?? prop.value;
    if (fixedValue != null && fixedValue !== "" && prop.selectedType !== 3) {
      const strValue = typeof fixedValue === "string" ? fixedValue : String(fixedValue);
      if (strValue.trim().length > 0) {
        result.push({ name: prop.name, value: strValue });
      }
    }
  }

  return result;
}

function extractKeyStyles(di: SdDisplayItem): Array<{ name: string; value: string }> {
  const styleObj = di.styleObj;
  if (!styleObj) return [];

  const result: Array<{ name: string; value: string }> = [];

  for (const { section, key } of KEY_STYLE_PATHS) {
    const sectionObj = styleObj[section];
    if (!sectionObj) continue;
    const value = sectionObj[key];
    if (value != null && value !== "") {
      result.push({ name: key, value: String(value) });
    }
  }

  return result;
}

function getChildNames(di: SdDisplayItem, limit: number): string[] {
  const children = di.children || [];
  return children.slice(0, limit).map((child) => child.friendlyName || child.item?.name || "Unknown");
}

function getParentName(dict: Record<number, SdDisplayItem>, di: SdDisplayItem): string | null {
  const parentId = di.parentId;
  if (parentId == null) return null;
  const parent = dict[parentId];
  return parent ? parent.friendlyName || parent.item?.name || null : null;
}

function expandComponent(ref: MentionReference, sd: SdInstance): string {
  const dict = sd.screenProvider?.state?.screenDesignDictionary;
  if (!dict) return `Component: ${ref.displayName} (not found)`;

  const di = findDisplayItemById(dict, ref.entityId);
  if (!di) return `Component: ${ref.displayName} (not found in current screen)`;

  const componentType = di.item?.name || "Unknown";
  const parentName = getParentName(dict, di);
  const keyProps = extractKeyProperties(di);
  const keyStyles = extractKeyStyles(di);
  const childNames = getChildNames(di, 5);

  const lines: string[] = [];
  lines.push(`Component: ${ref.displayName}`);
  lines.push(`  Type: ${componentType}`);
  if (parentName) lines.push(`  Parent: ${parentName}`);

  if (keyProps.length > 0) {
    lines.push("  Properties:");
    for (const p of keyProps) {
      lines.push(`    ${p.name}: ${p.value}`);
    }
  }

  if (keyStyles.length > 0) {
    lines.push("  Styles:");
    for (const s of keyStyles) {
      lines.push(`    ${s.name}: ${s.value}`);
    }
  }

  if (childNames.length > 0) {
    const totalChildren = di.children?.length || 0;
    const suffix = totalChildren > 5 ? ` (+${totalChildren - 5} more)` : "";
    lines.push(`  Children: ${childNames.join(", ")}${suffix}`);
  }

  return lines.join("\n");
}

function expandDatasource(ref: MentionReference, sd: SdInstance, includeIds: boolean): string {
  const datasources = getDatasources(sd);
  const ds = datasources.find((d) => d.id?.toString() === ref.entityId);
  if (!ds) return `Datasource: ${ref.displayName} (not found)`;

  const lines: string[] = [];
  lines.push(`Datasource: ${ds.name || ref.displayName}`);
  if (includeIds) lines.push(`  ID: ${ds.id}`);
  const dsTypeName = resolveDatasourceTypeName(ds.datasourceType);
  if (dsTypeName) lines.push(`  Type: ${dsTypeName}`);

  const tables = ds.tables || ds.schemas || [];
  if (tables.length > 0) {
    const shown = tables.slice(0, 10);
    lines.push(`  Tables/Schemas (${tables.length}):`);
    for (const t of shown) {
      const tableName = t.name || t.tableName || String(t);
      lines.push(`    - ${tableName}`);
    }
    if (tables.length > 10) {
      lines.push(`    ...and ${tables.length - 10} more`);
    }
  }

  return lines.join("\n");
}

function expandAction(ref: MentionReference, sd: SdInstance): string {
  const actionDefs = getActionDefinitions(sd);
  const actionDef = actionDefs.find((a) => a.id?.toString() === ref.entityId);
  if (!actionDef) return `Action: ${ref.displayName} (not found)`;

  const lines: string[] = [];
  lines.push(`Action: ${actionDef.displayName || actionDef.name || ref.displayName}`);

  const parentDsName = resolveDatasourceName(sd, actionDef.kDatasource_Id);
  if (parentDsName) lines.push(`  Datasource: ${parentDsName}`);
  const actTypeName = resolveDatasourceTypeName(actionDef.actionType);
  if (actTypeName) lines.push(`  Type: ${actTypeName}`);

  const params = actionDef.parameters || actionDef.actionParameters || [];
  if (params.length > 0) {
    lines.push(`  Parameters (${params.length}):`);
    for (const p of params) {
      const pName = p.name || p.parameterName || "unnamed";
      lines.push(`    - ${pName}${p.type ? ` (${p.type})` : ""}`);
    }
  }

  if (actionDef.statement) {
    const stmt = actionDef.statement.length > 200 ? actionDef.statement.substring(0, 200) + "..." : actionDef.statement;
    lines.push(`  Statement: ${stmt}`);
  }

  return lines.join("\n");
}

function expandScreen(ref: MentionReference, sd: SdInstance, includeIds: boolean): string {
  const screenList = sd.screenProvider?.state?.screenList || [];
  const screen = screenList.find((s) => s.id?.toString() === ref.entityId);
  if (!screen) return `Screen: ${ref.displayName} (not found)`;

  const currentScreenId = sd.screenProvider?.state?.sdId ?? null;
  const project = sd.projectProvider?.getProject?.() || sd.projectProvider?.state?.project;

  const lines: string[] = [];
  lines.push(`Screen: ${screen.name || ref.displayName}`);
  if (includeIds) lines.push(`  ID: ${screen.id}`);

  if (String(screen.id) === String(currentScreenId)) {
    lines.push("  Status: Currently open");
  }

  if (project?.startingScreenId != null && String(project.startingScreenId) === String(screen.id)) {
    lines.push("  Role: Starting screen");
  }
  if (project?.signinScreenId != null && String(project.signinScreenId) === String(screen.id)) {
    lines.push("  Role: Sign-in screen");
  }

  return lines.join("\n");
}

function expandError(ref: MentionReference, sd: SdInstance): string {
  const errors = getValidationErrors(sd);
  // entityId is "validation-{index}"
  const idxStr = ref.entityId.replace("validation-", "");
  const idx = parseInt(idxStr, 10);
  const err = errors[idx];
  if (!err) return `Validation Error: ${ref.displayName} (not found)`;

  const lines: string[] = [];
  const severity = err.type === "Error" ? "Error" : "Warning";
  lines.push(`Validation ${severity}:`);
  lines.push(`  Message: ${err.message}`);
  if (err.displayItem) lines.push(`  Component: ${err.displayItem}`);
  if (err.screenName) lines.push(`  Screen: ${err.screenName}`);
  if (err.event) lines.push(`  Event: ${err.event}`);
  if (err.workflowName) lines.push(`  Workflow: ${err.workflowName}`);
  if (err.workflowItem) lines.push(`  Workflow Item: ${err.workflowItem}`);
  if (err.schedulerJobName) lines.push(`  Scheduler Job: ${err.schedulerJobName}`);
  if (err.apiManagementName) lines.push(`  API: ${err.apiManagementName}`);
  if (err.csharpDatasourceName) lines.push(`  C# Datasource: ${err.csharpDatasourceName}`);
  if (err.csharpErrorMessages && err.csharpErrorMessages.length > 0) {
    lines.push("  C# Errors:");
    for (const msg of err.csharpErrorMessages.slice(0, 5)) {
      lines.push(`    - ${msg}`);
    }
    if (err.csharpErrorMessages.length > 5) {
      lines.push(`    ...and ${err.csharpErrorMessages.length - 5} more`);
    }
  }

  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Public: expandMentionContext
// ---------------------------------------------------------------------------

function expandLanguage(ref: MentionReference, sd: SdInstance): string {
  const languages = getProjectLanguages(sd);
  const lang = languages.find((l) => l.id === ref.entityId);
  if (!lang) return `Language: ${ref.displayName} (not found)`;

  const lines: string[] = [];
  lines.push(`Language: ${lang.shortName}`);
  lines.push(`  Locale: ${lang.name}`);
  if (lang.isDefault) lines.push("  Default: Yes");

  return lines.join("\n");
}

/**
 * Expands a mention reference into a human-readable context string.
 * @param includeIds When true, includes entity IDs (for AI consumption). Default false (for user-facing tooltips).
 */
export function expandMentionContext(ref: MentionReference, includeIds = false): string {
  const sd = getSd();
  if (!sd) return `${ref.category}: ${ref.displayName} (designer not available)`;

  switch (ref.category) {
    case "component":
      return expandComponent(ref, sd);
    case "datasource":
      return expandDatasource(ref, sd, includeIds);
    case "action":
      return expandAction(ref, sd);
    case "screen":
      return expandScreen(ref, sd, includeIds);
    case "error":
      return expandError(ref, sd);
    case "language":
      return expandLanguage(ref, sd);
    default:
      return `${ref.category}: ${ref.displayName}`;
  }
}
